import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { IAccount, IContactFieldOptions, ICustomSurvey, IRichContentEditorState } from '../../../../../extViewmodels';
import { ToolbarCustomSurvey } from '../../../../../models/UiUtils';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../components-v2/Select';
import {
	IRichContentDocumentEditorConfig,
	RichContentDocumentEditor,
} from '../../../../components/richContent/RichContentDocumentEditor';
import { TextInput } from '../../../../components/TextInput';
import { Toggle } from '../../../../components/Toggle';
import { grayIconFill } from '../../../../styles/colors';
import { FormFieldWrapper } from '../../shared/components/FormFieldWrapper';
import { ShowSurveyNameToggle } from '../../shared/components/ShowSurveyNameToggle';
import { TagOnCompletion } from '../../shared/components/TagOnCompletion';
import { usePreviewMode } from '../../shared/contexts/PreviewModeContext';
import { useSurveyHeaderCustomizationStatus } from '../../shared/hooks';
import { styleSheet } from '../styles';
import { CustomSurveyFormData } from '../utils';

const config: IRichContentDocumentEditorConfig = {
	plugins: 'autolink',
	toolbar: ToolbarCustomSurvey,
};

// Helper function to get current dropdown selection and display text based on form values
const getDropdownText = (collect: boolean, require: boolean): string => {
	if (require && collect) return 'Required';
	if (collect && !require) return 'Collect';
	return 'Do Not Collect';
};

// Helper function to handle dropdown selection changes
const handleDropdownChange = <T extends string>(
	value: string,
	fieldName: T,
	setValue: (name: T, value: IContactFieldOptions, options?: { shouldDirty?: boolean }) => void,
	currentUpdateExisting = false
) => {
	const contactOptions: IContactFieldOptions = {};

	switch (value) {
		case 'Required':
			contactOptions.collect = true;
			contactOptions.require = true;
			contactOptions.updateExisting = currentUpdateExisting;
			break;
		case 'Collect':
			contactOptions.collect = true;
			contactOptions.require = false;
			contactOptions.updateExisting = currentUpdateExisting;
			break;
		case 'Do Not Collect':
		default:
			contactOptions.collect = false;
			contactOptions.require = false;
			contactOptions.updateExisting = currentUpdateExisting;
			break;
	}

	setValue(fieldName, contactOptions, { shouldDirty: true });
};

export const BasicInfoSettings = ({ survey: _survey, account }: { survey: ICustomSurvey; account: IAccount }) => {
	const { control, watch, setValue } = useFormContext<CustomSurveyFormData>();
	const { isPreviewOnly } = usePreviewMode();
	const formValues = watch();

	const { data: customHeaderData, hasSurveyHeaderCustomization } = useSurveyHeaderCustomizationStatus();

	// Without a header customization for surveys the survey name is always rendered by the default
	// header, so the toggle is locked on (disabled, with an explanatory info tooltip) and the value is
	// forced on. The toggle only has an effect when a custom header replaces that default header. We
	// gate on `!error` so a failed status load — which also reports `hasCustomization: false` — does
	// not silently overwrite a value the user saved.
	React.useEffect(() => {
		if (customHeaderData && !customHeaderData.error && !hasSurveyHeaderCustomization) {
			setValue('options.showSurveyName', true);
		}
	}, [customHeaderData, hasSurveyHeaderCustomization, setValue]);

	return (
		<section className={css(styleSheet.tabsControllerWrap)}>
			<h3>Basic Information</h3>
			<FormFieldWrapper
				name='updateExistingContacts'
				control={control}
				title='Update Existing Contact Information'
				description='Overwrite existing contact data with new information from survey responses'
			>
				{({ value, onChange }) => (
					<Toggle
						id='update-existing-contacts-checkbox'
						isOn={!!value}
						disabled={isPreviewOnly}
						onToggleCheckChanged={() => {
							const newValue = !value;

							// Update the toggle state
							onChange(newValue);

							if (newValue) {
								// When turning ON: If any field is "Do Not Collect", change it to "Collect"
								// If it's already "Required", leave it as "Required"
								const currentOptions = formValues.options;

								// Helper to ensure at least collect is true
								const ensureCollecting = (fieldOptions?: Api.IContactFieldOptions): Api.IContactFieldOptions => {
									if (!fieldOptions?.collect) {
										// If not collecting, set to collect (but not required)
										return { collect: true, require: false, updateExisting: true };
									}
									// Already collecting (either collect or required), just update the updateExisting flag
									return { ...fieldOptions, updateExisting: true };
								};

								setValue('options.contactNameOptions', ensureCollecting(currentOptions?.contactNameOptions), {
									shouldDirty: true,
								});
								setValue('options.contactEmailOptions', ensureCollecting(currentOptions?.contactEmailOptions), {
									shouldDirty: true,
								});
								setValue(
									'options.contactPhoneNumberOptions',
									ensureCollecting(currentOptions?.contactPhoneNumberOptions),
									{ shouldDirty: true }
								);
								setValue('options.contactBirthdayOptions', ensureCollecting(currentOptions?.contactBirthdayOptions), {
									shouldDirty: true,
								});
								setValue('options.contactAddressOptions', ensureCollecting(currentOptions?.contactAddressOptions), {
									shouldDirty: true,
								});
							} else {
								// When turning OFF: Only update the updateExisting flag, leave dropdown selections as-is
								setValue(
									'options.contactNameOptions',
									{
										...formValues.options?.contactNameOptions,
										updateExisting: false,
									},
									{ shouldDirty: true }
								);
								setValue(
									'options.contactEmailOptions',
									{
										...formValues.options?.contactEmailOptions,
										updateExisting: false,
									},
									{ shouldDirty: true }
								);
								setValue(
									'options.contactPhoneNumberOptions',
									{
										...formValues.options?.contactPhoneNumberOptions,
										updateExisting: false,
									},
									{ shouldDirty: true }
								);
								setValue(
									'options.contactBirthdayOptions',
									{
										...formValues.options?.contactBirthdayOptions,
										updateExisting: false,
									},
									{ shouldDirty: true }
								);
								setValue(
									'options.contactAddressOptions',
									{
										...formValues.options?.contactAddressOptions,
										updateExisting: false,
									},
									{ shouldDirty: true }
								);
							}
						}}
						uncheckedColor={grayIconFill}
					/>
				)}
			</FormFieldWrapper>
			<FormFieldWrapper
				name='options.contactNameOptions'
				control={control}
				title='Contact Name'
				description='Collect first and last name from respondents'
			>
				{() => {
					const nameOptions = formValues.options?.contactNameOptions;
					const currentSelection = getDropdownText(nameOptions?.collect ?? false, nameOptions?.require ?? false);

					return (
						<Select
							value={currentSelection}
							disabled={isPreviewOnly}
							onValueChange={(value: string) => {
								handleDropdownChange(
									value,
									'options.contactNameOptions',
									setValue,
									nameOptions?.updateExisting ?? false
								);
							}}
						>
							<SelectTrigger id='contact-name-dropdown'>
								<SelectValue placeholder='Select an option'>{currentSelection}</SelectValue>
							</SelectTrigger>
							<SelectContent>
								<SelectItem value='Do Not Collect'>Do Not Collect</SelectItem>
								<SelectItem value='Collect'>Collect</SelectItem>
								<SelectItem value='Required'>Required</SelectItem>
							</SelectContent>
						</Select>
					);
				}}
			</FormFieldWrapper>
			<FormFieldWrapper
				name='options.contactEmailOptions'
				control={control}
				title='Contact Email'
				description='Collect email address from respondents'
			>
				{() => {
					const emailOptions = formValues.options?.contactEmailOptions;
					const currentSelection = getDropdownText(emailOptions?.collect ?? false, emailOptions?.require ?? false);

					return (
						<Select
							value={currentSelection}
							disabled={isPreviewOnly}
							onValueChange={(value: string) => {
								handleDropdownChange(
									value,
									'options.contactEmailOptions',
									setValue,
									emailOptions?.updateExisting ?? false
								);
							}}
						>
							<SelectTrigger id='contact-email-dropdown'>
								<SelectValue placeholder='Select an option'>{currentSelection}</SelectValue>
							</SelectTrigger>
							<SelectContent>
								<SelectItem value='Do Not Collect'>Do Not Collect</SelectItem>
								<SelectItem value='Collect'>Collect</SelectItem>
								<SelectItem value='Required'>Required</SelectItem>
							</SelectContent>
						</Select>
					);
				}}
			</FormFieldWrapper>
			<FormFieldWrapper
				name='options.contactPhoneNumberOptions'
				control={control}
				title='Contact Phone Number'
				description='Collect phone number from respondents'
			>
				{() => {
					const phoneOptions = formValues.options?.contactPhoneNumberOptions;
					const currentSelection = getDropdownText(phoneOptions?.collect ?? false, phoneOptions?.require ?? false);

					return (
						<Select
							value={currentSelection}
							disabled={isPreviewOnly}
							onValueChange={(value: string) => {
								handleDropdownChange(
									value,
									'options.contactPhoneNumberOptions',
									setValue,
									phoneOptions?.updateExisting ?? false
								);
							}}
						>
							<SelectTrigger id='contact-phone-dropdown'>
								<SelectValue placeholder='Select an option'>{currentSelection}</SelectValue>
							</SelectTrigger>
							<SelectContent>
								<SelectItem value='Do Not Collect'>Do Not Collect</SelectItem>
								<SelectItem value='Collect'>Collect</SelectItem>
								<SelectItem value='Required'>Required</SelectItem>
							</SelectContent>
						</Select>
					);
				}}
			</FormFieldWrapper>
			<FormFieldWrapper
				name='options.contactBirthdayOptions'
				control={control}
				title='Contact Birthday'
				description='Collect birthday from respondents'
			>
				{() => {
					const birthdayOptions = formValues.options?.contactBirthdayOptions;
					const currentSelection = getDropdownText(
						birthdayOptions?.collect ?? false,
						birthdayOptions?.require ?? false
					);

					return (
						<Select
							value={currentSelection}
							disabled={isPreviewOnly}
							onValueChange={(value: string) => {
								handleDropdownChange(
									value,
									'options.contactBirthdayOptions',
									setValue,
									birthdayOptions?.updateExisting ?? false
								);
							}}
						>
							<SelectTrigger id='contact-birthday-dropdown'>
								<SelectValue placeholder='Select an option'>{currentSelection}</SelectValue>
							</SelectTrigger>
							<SelectContent>
								<SelectItem value='Do Not Collect'>Do Not Collect</SelectItem>
								<SelectItem value='Collect'>Collect</SelectItem>
								<SelectItem value='Required'>Required</SelectItem>
							</SelectContent>
						</Select>
					);
				}}
			</FormFieldWrapper>
			<FormFieldWrapper
				name='options.contactAddressOptions'
				control={control}
				title='Contact Address'
				description='Collect mailing address from respondents'
			>
				{() => {
					const addressOptions = formValues.options?.contactAddressOptions;
					const currentSelection = getDropdownText(addressOptions?.collect ?? false, addressOptions?.require ?? false);

					return (
						<Select
							value={currentSelection}
							disabled={isPreviewOnly}
							onValueChange={(value: string) => {
								handleDropdownChange(
									value,
									'options.contactAddressOptions',
									setValue,
									addressOptions?.updateExisting ?? false
								);
							}}
						>
							<SelectTrigger id='contact-address-dropdown'>
								<SelectValue placeholder='Select an option'>{currentSelection}</SelectValue>
							</SelectTrigger>
							<SelectContent>
								<SelectItem value='Do Not Collect'>Do Not Collect</SelectItem>
								<SelectItem value='Collect'>Collect</SelectItem>
								<SelectItem value='Required'>Required</SelectItem>
							</SelectContent>
						</Select>
					);
				}}
			</FormFieldWrapper>
			<FormFieldWrapper
				name='options.sendConfirmationMessage'
				control={control}
				title='Send Confirmation Message'
				description='Send an email confirmation to respondents after survey submission'
			>
				{({ value, onChange }) => (
					<Toggle
						id='send-confirmation-message-checkbox'
						isOn={value}
						disabled={isPreviewOnly}
						onToggleCheckChanged={() => onChange(!value)}
						uncheckedColor={grayIconFill}
					/>
				)}
			</FormFieldWrapper>
			{/* The company logo only renders in the default header. A custom header replaces that header
			    entirely, so the toggle would do nothing and is hidden — the inverse of the survey-name
			    toggle below, which only has an effect when a custom header exists. */}
			{account?.preferences?.logo?.url && !hasSurveyHeaderCustomization && (
				<FormFieldWrapper
					name='options.includeCompanyLogo'
					control={control}
					title='Include Company Logo'
					description='Display your company logo at the top of the survey'
				>
					{({ value, onChange }) => (
						<Toggle
							id='include-company-logo-checkbox'
							isOn={value}
							disabled={isPreviewOnly}
							onToggleCheckChanged={() => onChange(!value)}
							uncheckedColor={grayIconFill}
						/>
					)}
				</FormFieldWrapper>
			)}
			<ShowSurveyNameToggle<CustomSurveyFormData>
				name='options.showSurveyName'
				control={control}
				description='Display the survey name at the top of the survey'
				id='show-survey-name-checkbox'
				disabled={isPreviewOnly}
				lockedReason={
					hasSurveyHeaderCustomization
						? undefined
						: 'The survey name is always shown at the top of the survey. This setting only takes effect when a custom survey header is applied to your account.'
				}
			/>
			<TagOnCompletion />
			<div className={css(styleSheet.formControl)}>
				<label htmlFor='intro'>Intro message</label>
				<Controller
					name='options.intro'
					control={control}
					render={({ field: { ref, ...fieldProps } }) => (
						<RichContentDocumentEditor
							className={css(styleSheet.editor)}
							config={config}
							contentState={formValues?.options?.intro as IRichContentEditorState}
							onContentStateChanged={fieldProps.onChange}
							readOnly={isPreviewOnly}
							readOnlyUseFullEditor={true}
							{...fieldProps}
						/>
					)}
				/>
			</div>
			<div className={css(styleSheet.formControl)}>
				<label htmlFor='companyName'>Company Name</label>
				<Controller
					name='options.companyName'
					control={control}
					render={({ field: { ref, ...fieldProps } }) => (
						<TextInput inputId='companyName' type='text' disabled={isPreviewOnly} {...fieldProps} />
					)}
				/>
			</div>
		</section>
	);
};
